import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ErrorType,
  CategorizedError,
  categorizeError,
  generateErrorId,
  groupErrorsByPriority,
} from '@amzn/sss-website-theme';

import { toError, logError } from 'src/logger';

export interface ErrorHandlerConfig {
  onSuccess?: (response: any) => void;
  onClientError?: (error: Error) => void;
  onServerError?: (error: Error) => void;
  onNotFoundError?: (error: Error) => void;
  onNetworkError?: (error: Error) => void;
  onValidationError?: (error: Error) => void;
  redirectOnServerError?: string;
  showBannerOnClientError?: boolean;
  autoNavigateOnSuccess?: string;
  componentName?: string; // For unified logging context
}

export interface ErrorState {
  errors: CategorizedError[];
  hasErrors: boolean;
  primaryError: Error | null;
  hasClientErrors: boolean;
  hasServerErrors: boolean;
  hasNetworkErrors: boolean;
}

export const useUnifiedErrorHandler = (config: ErrorHandlerConfig = {}) => {
  const [errorState, setErrorState] = useState<ErrorState>({
    errors: [],
    hasErrors: false,
    primaryError: null,
    hasClientErrors: false,
    hasServerErrors: false,
    hasNetworkErrors: false,
  });

  const navigate = useNavigate();
  const configRef = useRef(config);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const updateErrorState = useCallback((errors: CategorizedError[]) => {
    setErrorState({
      errors,
      hasErrors: errors.length > 0,
      primaryError: errors[0]?.error || null,
      hasClientErrors: errors.some((e) => e.type === ErrorType.CLIENT),
      hasServerErrors: errors.some((e) => e.type === ErrorType.SERVER),
      hasNetworkErrors: errors.some((e) => e.type === ErrorType.NETWORK),
    });
  }, []);

  const handleError = useCallback((errorInput: unknown, context?: string) => {
    const error = toError(errorInput);
    const errorType = categorizeError(error);
    const errorId = generateErrorId();

    const categorizedError: CategorizedError = {
      id: errorId,
      error,
      type: errorType,
      timestamp: Date.now(),
      context,
    };

    // Unified logging for all error types
    const componentName = configRef.current.componentName || 'UnknownComponent';
    const contextMessage = context ? ` (${context})` : '';

    switch (errorType) {
      case ErrorType.SERVER:
        logError(componentName, `Server error occurred${contextMessage}:`, error);
        break;
      case ErrorType.CLIENT:
        logError(componentName, `Client error occurred${contextMessage}:`, error);
        break;
      case ErrorType.NOT_FOUND:
        logError(componentName, `Not found error occurred${contextMessage}:`, error);
        break;
      case ErrorType.NETWORK:
        logError(componentName, `Network error occurred${contextMessage}:`, error);
        break;
      case ErrorType.VALIDATION:
        logError(componentName, `Validation error occurred${contextMessage}:`, error);
        break;
      default:
        logError(componentName, `Unknown error occurred${contextMessage}:`, error);
        break;
    }

    // Add to error collection
    setErrorState((prev) => {
      const newErrors = [...prev.errors, categorizedError];
      return {
        errors: newErrors,
        hasErrors: true,
        primaryError: prev.primaryError || error,
        hasClientErrors: newErrors.some((e) => e.type === ErrorType.CLIENT),
        hasServerErrors: newErrors.some((e) => e.type === ErrorType.SERVER),
        hasNetworkErrors: newErrors.some((e) => e.type === ErrorType.NETWORK),
      };
    });

    // Execute appropriate handler based on error type
    switch (errorType) {
      case ErrorType.SERVER:
        configRef.current.onServerError?.(error);
        break;
      case ErrorType.CLIENT:
        configRef.current.onClientError?.(error);
        break;
      case ErrorType.NOT_FOUND:
        configRef.current.onNotFoundError?.(error);
        break;
      case ErrorType.NETWORK:
        configRef.current.onNetworkError?.(error);
        break;
      case ErrorType.VALIDATION:
        configRef.current.onValidationError?.(error);
        break;
      default:
        configRef.current.onClientError?.(error);
        break;
    }
  }, []);

  const clearErrors = useCallback(() => {
    updateErrorState([]);
  }, [updateErrorState]);

  const handleSuccess = useCallback(
    (response: any) => {
      // Clear errors on success
      clearErrors();

      // Execute success handler
      configRef.current.onSuccess?.(response);

      // Auto-navigate if configured
      if (configRef.current.autoNavigateOnSuccess) {
        navigate(configRef.current.autoNavigateOnSuccess);
      }
    },
    [navigate, clearErrors]
  );

  const removeError = useCallback((errorId: string) => {
    setErrorState((prev) => {
      const filteredErrors = prev.errors.filter((e) => e.id !== errorId);
      return {
        errors: filteredErrors,
        hasErrors: filteredErrors.length > 0,
        primaryError: filteredErrors[0]?.error || null,
        hasClientErrors: filteredErrors.some((e) => e.type === ErrorType.CLIENT),
        hasServerErrors: filteredErrors.some((e) => e.type === ErrorType.SERVER),
        hasNetworkErrors: filteredErrors.some((e) => e.type === ErrorType.NETWORK),
      };
    });
  }, []);

  const removeErrorsByContext = useCallback((context: string) => {
    setErrorState((prev) => {
      const filteredErrors = prev.errors.filter((e) => e.context !== context);
      return {
        errors: filteredErrors,
        hasErrors: filteredErrors.length > 0,
        primaryError: filteredErrors[0]?.error || null,
        hasClientErrors: filteredErrors.some((e) => e.type === ErrorType.CLIENT),
        hasServerErrors: filteredErrors.some((e) => e.type === ErrorType.SERVER),
        hasNetworkErrors: filteredErrors.some((e) => e.type === ErrorType.NETWORK),
      };
    });
  }, []);

  const getErrorsByType = useCallback(
    (type: ErrorType) => errorState.errors.filter((error) => error.type === type),
    [errorState.errors]
  );

  const getErrorsByContext = useCallback(
    (context: string) => errorState.errors.filter((error) => error.context === context),
    [errorState.errors]
  );

  const handleMultipleErrors = useCallback(
    (errors: unknown[], contexts?: string[]) => {
      errors.forEach((error, index) => {
        const context = contexts?.[index];
        handleError(error, context);
      });
    },
    [handleError]
  );

  const getGroupedErrors = useCallback(() => groupErrorsByPriority(errorState.errors), [errorState.errors]);

  const getPrimaryError = useCallback(() => {
    const prioritizedErrors = groupErrorsByPriority(errorState.errors);
    return prioritizedErrors[0] || null;
  }, [errorState.errors]);

  return {
    errorState,
    handleError,
    handleSuccess,
    handleMultipleErrors,
    clearErrors,
    removeError,
    removeErrorsByContext,
    getErrorsByType,
    getErrorsByContext,
    getGroupedErrors,
    getPrimaryError,
  };
};
